import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";

interface OrdersFilterProps {
  onFilterChange: (filters: {
    status_id?: number;
    order_type?: string;
    start_date?: string;
    end_date?: string;
  }) => void;
  initialFilters?: {
    status_id?: number;
    order_type?: string;
    start_date?: string;
    end_date?: string;
  };
}

const OrdersFilter: React.FC<OrdersFilterProps> = ({
  onFilterChange,
  initialFilters = {},
}) => {
  // State for filter values
  const [filters, setFilters] = useState({
    status_id: initialFilters.status_id || undefined,
    order_type: initialFilters.order_type || undefined,
    start_date: initialFilters.start_date || "",
    end_date: initialFilters.end_date || "",
  });

  // Set initial filters
  useEffect(() => {
    setFilters({
      status_id: initialFilters.status_id || undefined,
      order_type: initialFilters.order_type || undefined,
      start_date: initialFilters.start_date || "",
      end_date: initialFilters.end_date || "",
    });
  }, [initialFilters]);

  // Update filter state
  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    // Format dates properly for API
    const formattedFilters = {
      ...filters,
      start_date: filters.start_date ? filters.start_date : undefined,
      end_date: filters.end_date ? filters.end_date : undefined,
    };

    onFilterChange(formattedFilters);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status_id: undefined,
      order_type: undefined,
      start_date: "",
      end_date: "",
    });

    onFilterChange({});
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        Filters
      </h3>

      <div className="space-y-4">
        {/* Order Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Order Status
          </label>
          <select
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-2 px-3 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={filters.status_id || ""}
            onChange={(e) =>
              handleFilterChange(
                "status_id",
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
          >
            <option value="">All Statuses</option>
            <option value="1">New</option>
            <option value="2">Processing</option>
            <option value="3">Ready</option>
            <option value="4">Out for Delivery</option>
            <option value="5">Delivered</option>
            <option value="6">Picked Up</option>
            <option value="7">Cancelled</option>
          </select>
        </div>

        {/* Order Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Order Type
          </label>
          <select
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-2 px-3 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={filters.order_type || ""}
            onChange={(e) =>
              handleFilterChange("order_type", e.target.value || undefined)
            }
          >
            <option value="">All Types</option>
            <option value="pickup">Pickup</option>
            <option value="delivery">Delivery</option>
          </select>
        </div>

        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Start Date
          </label>
          <Input
            className="w-full"
            type="date"
            value={filters.start_date}
            onChange={(e) => handleFilterChange("start_date", e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            End Date
          </label>
          <Input
            className="w-full"
            type="date"
            value={filters.end_date}
            onChange={(e) => handleFilterChange("end_date", e.target.value)}
          />
        </div>

        {/* Quick Date Filters */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            variant="light"
            onPress={() => {
              const today = new Date();
              const formattedDate = today.toISOString().split("T")[0];

              setFilters((prev) => ({
                ...prev,
                start_date: formattedDate,
                end_date: formattedDate,
              }));
            }}
          >
            Today
          </Button>

          <Button
            size="sm"
            variant="light"
            onPress={() => {
              const today = new Date();
              const yesterday = new Date(today);

              yesterday.setDate(yesterday.getDate() - 1);
              const formattedDate = yesterday.toISOString().split("T")[0];

              setFilters((prev) => ({
                ...prev,
                start_date: formattedDate,
                end_date: formattedDate,
              }));
            }}
          >
            Yesterday
          </Button>

          <Button
            size="sm"
            variant="light"
            onPress={() => {
              const today = new Date();
              const startOfWeek = new Date(today);

              startOfWeek.setDate(today.getDate() - today.getDay());
              setFilters((prev) => ({
                ...prev,
                start_date: startOfWeek.toISOString().split("T")[0],
                end_date: today.toISOString().split("T")[0],
              }));
            }}
          >
            This Week
          </Button>

          <Button
            size="sm"
            variant="light"
            onPress={() => {
              const today = new Date();
              const startOfMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1,
              );

              setFilters((prev) => ({
                ...prev,
                start_date: startOfMonth.toISOString().split("T")[0],
                end_date: today.toISOString().split("T")[0],
              }));
            }}
          >
            This Month
          </Button>
        </div>

        {/* Apply/Reset Buttons */}
        <div className="flex flex-col gap-2 pt-2">
          <Button color="primary" onPress={applyFilters}>
            Apply Filters
          </Button>

          <Button variant="light" onPress={resetFilters}>
            Reset Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrdersFilter;
